/**
 * Public entry for MorningReport learners: why it's worth their time, and a
 * clear way in. Signing up from here creates a `learner` account, which sees
 * only MorningReport. The role hint goes to localStorage, like the /share
 * provider flow, so it survives the email verification round trip on the
 * same device.
 */
import React from "react";
import PropTypes from "prop-types";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import HeadphonesRounded from "@mui/icons-material/HeadphonesRounded";
import PriorityHighRounded from "@mui/icons-material/PriorityHighRounded";
import FactCheckRounded from "@mui/icons-material/FactCheckRounded";
import { StepperPageShell } from "components/common/stepperPage";
import logoIcon from "assets/images/logos/logo-icon.png";
import { useAuth } from "hooks/useAuth";
import { MORNING_REPORT_GUEST_PATH, MORNING_REPORT_PATH } from "../morningReportPaths";

const BENEFITS = [
  {
    Icon: HeadphonesRounded,
    title: "Train your ear",
    body: "A clinician and a patient talk through the visit. Listen only, or follow along with the script.",
  },
  {
    Icon: PriorityHighRounded,
    title: "See what you missed",
    body: "Your differential is checked against the case's answer key, with the must-not-miss diagnoses flagged.",
  },
  {
    Icon: FactCheckRounded,
    title: "Work the whole case",
    body: "Review the workup, commit to a diagnosis, then see the treatment, teaching pearls, discharge plan and note.",
  },
];

function Cta({ onStart, onSignIn }) {
  return (
    <Stack alignItems="center" spacing={1}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        <Button variant="contained" size="large" onClick={onStart}>
          Try today's case free
        </Button>
        <Button size="large" onClick={onSignIn}>
          I have an account
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Today's case is free. Subscribe for $9 a month to play all cases. Cancel anytime.
      </Typography>
    </Stack>
  );
}

Cta.propTypes = {
  onStart: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
};

export default function MorningReportLandingPage() {
  const { user, userLoading } = useAuth();
  const navigate = useNavigate();

  if (userLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  // A guest (anonymous, from "Try today's case free") still sees this page.
  if (user && !user.isAnonymous) return <Navigate to={MORNING_REPORT_PATH} replace />;

  const signIn = () => {
    localStorage.setItem("pendingSignupRole", "learner");
    navigate("/auth");
  };
  // No account needed: the case page signs them in as a guest.
  const tryFree = () => navigate(MORNING_REPORT_GUEST_PATH);

  return (
    <StepperPageShell standalone maxWidth={700}>
      <Stack spacing={6} sx={{ py: { xs: 2, sm: 4 } }}>
        <Stack alignItems="center" spacing={2} sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={logoIcon}
            alt="Fusion MSI"
            sx={{ height: 48 }}
          />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
            Emergency medicine cases you learn by listening
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            Listen in as a clinician and patient work through an emergency
            department visit. List the diagnoses you'd consider, then Medical
            SuperIntelligence shows you how your thinking compares with the
            answer key. Made for medical students, residents, PAs and NPs.
          </Typography>
          <Box sx={{ pt: 1 }}>
            <Cta onStart={tryFree} onSignIn={signIn} />
          </Box>
        </Stack>

        <Divider />

        <Box>
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 1.5 }}
          >
            Why listen instead of read?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            Real patients tell their story out of order, and the important
            detail can be one sentence in the middle. Listening to the history
            is a different skill from reading a written case, and it gets better
            with practice.
          </Typography>
        </Box>

        <Stack spacing={3}>
          {BENEFITS.map(({ Icon, title, body }) => (
            <Stack
              key={title}
              direction="row"
              spacing={2}
              alignItems="flex-start"
            >
              <Icon color="primary" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {body}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>

        <Divider />

        <Cta onStart={tryFree} onSignIn={signIn} />
      </Stack>
    </StepperPageShell>
  );
}
